import { checkCompatibility, languageChain, parseManifest } from "./manifest";
import {
  CatalogError,
  type ArtifactEntry,
  type CatalogSet,
  type ClientIdentity,
  type DigestProvider,
  type FetchLike,
  type Manifest,
  type Messages,
  type NamespaceBundles,
  type VerifiedArtifact,
} from "./types";

export interface LoaderOptions {
  readonly fetch: FetchLike;
  readonly crypto: DigestProvider;
  /** Page origin; every catalog URL must stay on it. */
  readonly origin: string;
  /** Same-origin path prefix of the release, "/l10n". */
  readonly basePath: string;
  /** Per request; a hung request ends as a timeout instead of a stuck loading state. */
  readonly timeoutMs: number;
  readonly maxManifestBytes: number;
  readonly maxArtifactBytes: number;
}

export const DEFAULT_BASE_PATH = "/l10n";
/** Private tester preview release (same v1 files), served only to admitted signed-in testers. */
export const PREVIEW_BASE_PATH = "/api/l10n/preview";

const utf8 = new TextDecoder("utf-8", { fatal: true });

/** Absolute URL of a release path; throws url_rejected unless it is same-origin and under basePath. */
export function catalogUrl(options: Pick<LoaderOptions, "origin" | "basePath">, relative: string): string {
  const { origin, basePath } = options;
  if (!/^\/[A-Za-z0-9/_-]*[A-Za-z0-9_-]$/.test(basePath)) throw new CatalogError("url_rejected", `base path ${basePath}`);
  if (!/^[A-Za-z0-9][A-Za-z0-9._/-]*$/.test(relative) || relative.split("/").some((part) => part === "" || part === "." || part === "..")) {
    throw new CatalogError("url_rejected", relative);
  }
  const url = new URL(`${basePath}/${relative}`, origin);
  if (url.origin !== new URL(origin).origin || !url.pathname.startsWith(`${basePath}/`)) throw new CatalogError("url_rejected", relative);
  return url.href;
}

export function manifestUrl(options: Pick<LoaderOptions, "origin" | "basePath">, identity: ClientIdentity): string {
  return catalogUrl(options, `${identity.clientId}/v${identity.contractVersion}/manifest.json`);
}

function hex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, "0")).join("");
}

export async function sha256Hex(crypto: DigestProvider, bytes: ArrayBuffer): Promise<string> {
  return hex(await crypto.subtle.digest("SHA-256", bytes));
}

/** Combines the caller's signal with a per-request timeout. */
function withTimeout(signal: AbortSignal | undefined, timeoutMs: number): { signal: AbortSignal; timedOut: () => boolean; done: () => void } {
  const controller = new AbortController();
  let expired = false;
  const timer = setTimeout(() => {
    expired = true;
    controller.abort();
  }, timeoutMs);
  const onAbort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener("abort", onAbort, { once: true });
  return {
    signal: controller.signal,
    timedOut: () => expired,
    done: () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    },
  };
}

/** GETs a same-origin JSON file and returns its bytes after status, origin, MIME and size checks. */
async function fetchJsonBytes(options: LoaderOptions, url: string, maxBytes: number, init: RequestInit, signal?: AbortSignal): Promise<ArrayBuffer> {
  const timed = withTimeout(signal, options.timeoutMs);
  try {
    let response: Response;
    try {
      response = await options.fetch(url, { ...init, method: "GET", credentials: "omit", redirect: "error", signal: timed.signal });
    } catch {
      if (timed.timedOut()) throw new CatalogError("timeout", url);
      if (signal?.aborted) throw new CatalogError("aborted", url);
      throw new CatalogError("network", url);
    }
    if (response.status !== 200) throw new CatalogError("http_status", `${response.status} ${url}`, response.status);
    if (response.url && new URL(response.url).origin !== new URL(options.origin).origin) throw new CatalogError("url_rejected", response.url);
    const type = (response.headers.get("content-type") ?? "").split(";")[0].trim().toLowerCase();
    // The SPA catch-all answers unknown paths with 200 text/html; never parse that.
    if (type !== "application/json") throw new CatalogError("content_type", `${type || "none"} ${url}`);
    const declared = Number(response.headers.get("content-length"));
    if (Number.isFinite(declared) && declared > maxBytes) throw new CatalogError("too_large", url);
    let body: ArrayBuffer;
    try {
      body = await response.arrayBuffer();
    } catch {
      if (timed.timedOut()) throw new CatalogError("timeout", url);
      if (signal?.aborted) throw new CatalogError("aborted", url);
      throw new CatalogError("network", url);
    }
    if (body.byteLength > maxBytes) throw new CatalogError("too_large", url);
    return body;
  } finally {
    timed.done();
  }
}

function parseJson(bytes: ArrayBuffer, code: "manifest_invalid" | "artifact_invalid", what: string): { text: string; value: unknown } {
  try {
    const text = utf8.decode(bytes);
    return { text, value: JSON.parse(text) };
  } catch {
    throw new CatalogError(code, `${what} is not UTF-8 JSON`);
  }
}

/** Fetches, schema-checks and compatibility-checks the manifest the client's contract pointer names. */
export async function fetchManifest(options: LoaderOptions, identity: ClientIdentity, signal?: AbortSignal): Promise<Manifest> {
  const url = manifestUrl(options, identity);
  // no-cache: the pointer changes on release and rollback; the server answers 304 when unchanged.
  const bytes = await fetchJsonBytes(options, url, options.maxManifestBytes, { cache: "no-cache" }, signal);
  const manifest = parseManifest(parseJson(bytes, "manifest_invalid", url).value);
  checkCompatibility(manifest, identity);
  return manifest;
}

/** Messages of one artifact; throws artifact_invalid unless it is a flat string map of its own namespace. */
export function parseArtifactMessages(text: string, namespace: string): Messages {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    throw new CatalogError("artifact_invalid", `${namespace} is not JSON`);
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new CatalogError("artifact_invalid", `${namespace} is not an object`);
  const messages: Record<string, string> = {};
  for (const [key, message] of Object.entries(value)) {
    if (!key.startsWith(`${namespace}.`) || typeof message !== "string") throw new CatalogError("artifact_invalid", `${namespace} key ${key}`);
    messages[key] = message;
  }
  return messages;
}

/** Checks bytes against a manifest entry: exact length and sha256. */
export async function verifyArtifactBytes(crypto: DigestProvider, entry: ArtifactEntry, bytes: ArrayBuffer): Promise<void> {
  if (bytes.byteLength !== entry.bytes) throw new CatalogError("integrity", `${entry.path} has ${bytes.byteLength} bytes, expected ${entry.bytes}`);
  if ((await sha256Hex(crypto, bytes)) !== entry.sha256) throw new CatalogError("integrity", `${entry.path} sha256 mismatch`);
}

/** Artifacts a language set needs: every declared namespace of every language in the chain that the release has. */
export function requiredArtifacts(manifest: Manifest, identity: ClientIdentity, chain: readonly string[]): { language: string; namespace: string; entry: ArtifactEntry }[] {
  const byLanguage = manifest.clients[identity.clientId] ?? {};
  const out: { language: string; namespace: string; entry: ArtifactEntry }[] = [];
  for (const language of chain) {
    for (const namespace of identity.namespaces) {
      const entry = byLanguage[language]?.[namespace];
      if (entry) out.push({ language, namespace, entry });
    }
  }
  return out;
}

/** Builds a CatalogSet from verified artifacts; namespaces the client did not declare are never included. */
export function assembleSet(manifest: Pick<Manifest, "catalog_revision" | "base_language" | "languages" | "fallback">, language: string, artifacts: readonly VerifiedArtifact[]): CatalogSet {
  const bundles: Record<string, Record<string, Messages>> = {};
  for (const a of artifacts) (bundles[a.language] ??= {})[a.namespace] = parseArtifactMessages(a.text, a.namespace);
  return {
    revision: manifest.catalog_revision,
    language,
    chain: languageChain(manifest, language),
    baseLanguage: manifest.base_language,
    languages: [...manifest.languages],
    fallback: manifest.fallback,
    bundles: bundles as Record<string, NamespaceBundles>,
    artifacts,
  };
}

/**
 * Loads every artifact of `language` and its fallback chain for this client
 * and returns the complete set, or throws: a set is never partial.
 */
export async function loadCatalogSet(options: LoaderOptions, manifest: Manifest, identity: ClientIdentity, language: string, signal?: AbortSignal): Promise<CatalogSet> {
  const chain = languageChain(manifest, language);
  // The first failure cancels the remaining requests of the set.
  const siblings = new AbortController();
  const onAbort = () => siblings.abort();
  if (signal?.aborted) siblings.abort();
  else signal?.addEventListener("abort", onAbort, { once: true });
  try {
    const verified = await Promise.all(
      requiredArtifacts(manifest, identity, chain).map(async ({ language: lang, namespace, entry }) => {
        try {
          const bytes = await fetchJsonBytes(options, catalogUrl(options, entry.path), Math.min(options.maxArtifactBytes, entry.bytes), {}, siblings.signal);
          await verifyArtifactBytes(options.crypto, entry, bytes);
          const { text } = parseJson(bytes, "artifact_invalid", entry.path);
          parseArtifactMessages(text, namespace);
          return { language: lang, namespace, entry, text };
        } catch (e) {
          siblings.abort();
          throw e;
        }
      }),
    );
    return assembleSet(manifest, language, verified);
  } finally {
    signal?.removeEventListener("abort", onAbort);
  }
}
